import React from "react";

const TradeOrganizationCard = ({
  trades,
  tradesID,
  setError,
  setTrades,
  setSuccess,
  setTradesID,
  setIsTradeChanges,
  localTradeOrganizations,
}) => {
  const total = localTradeOrganizations?.length ?? 0;
  const selectedCount = trades?.length ?? 0;

  const clearStatus = () => {
    setIsTradeChanges?.(1);
    setError?.(null);
    setSuccess?.(null);
  };

  const toggleOne = (item) => {
    const nameStr = String(item?.name);
    const id = item?.trade_organization_id;

    if (trades?.includes(nameStr)) {
      setTrades(trades.filter((n) => n !== nameStr));
      setTradesID(tradesID.filter((tid) => tid !== id));
    } else {
      setTrades([...(trades || []), nameStr]);
      setTradesID([...(tradesID || []), id]);
    }
    clearStatus();
  };

  const selectAll = () => {
    if (!localTradeOrganizations?.length) return;
    setTrades(localTradeOrganizations.map((i) => String(i?.name)));
    setTradesID(localTradeOrganizations.map((i) => i?.trade_organization_id));
    clearStatus();
  };

  const clearAll = () => {
    setTrades([]);
    setTradesID([]);
    clearStatus();
  };

  return (
    <section className="w-full">
      {/* Header */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Trade Organizations</h3>
          <p className="text-[11px] text-gray-600">
            Select all organizations that apply. Click a card to toggle.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold text-gray-800">
            Selected: {selectedCount}/{total}
          </span>
          <div className="hidden sm:flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Mobile quick actions */}
      <div className="mb-3 flex gap-2 sm:hidden">
        <button
          type="button"
          onClick={selectAll}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-semibold text-gray-700 hover:bg-gray-50"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-semibold text-gray-700 hover:bg-gray-50"
        >
          Clear
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {localTradeOrganizations && localTradeOrganizations.length > 0 ? (
          localTradeOrganizations.map((org, idx) => {
            const isSelected = trades?.includes(String(org?.name));

            return (
              <article
                key={idx}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => toggleOne(org)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleOne(org);
                  }
                }}
                className={`group relative w-full cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition
                  ${isSelected ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200 hover:border-gray-300"}
                `}
              >
                {/* Selection badge (inline SVG) */}
                <div
                  className={`absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center rounded-full border text-white shadow-sm
                    ${isSelected ? "border-emerald-600 bg-emerald-600" : "border-gray-300 bg-white text-gray-400"}
                  `}
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    className="h-3.5 w-3.5"
                  >
                    <path d="M5 10.5l3 3 7-7" />
                  </svg>
                </div>

                <h4 className="pr-8 text-sm font-semibold text-gray-900">
                  {org?.name}
                </h4>
                <p className="mt-1 text-xs leading-relaxed text-gray-600 line-clamp-3">
                  {org?.description || "—"}
                </p>

                <div className="mt-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold
                      ${isSelected ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-700"}
                    `}
                  >
                    {isSelected ? "Selected" : "Tap to select"}
                  </span>
                </div>
              </article>
            );
          })
        ) : (
          <div className="col-span-full rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-xs text-gray-600">
            No Trade Organizations Found
          </div>
        )}
      </div>
    </section>
  );
};

export default TradeOrganizationCard;
